import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { responsiveStyles } from '@polestar/component-warehouse-react/helpers/responsiveStyle';
import Link from '@polestar/component-warehouse-react/components/link/Link';
import { motion } from 'framer-motion';
import { MotionDurationsSeconds, createTransition, } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { SideNavigationList } from './SideNavigationList';
import SideNavigationContainer from '@polestar/component-warehouse-react/components/side-navigation/internal/SideNavigationContainer';
import warnOnce from '@polestar/component-warehouse-react/helpers/warnOnce';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
const SideNavigationAnchor = forwardRef(({ items, monochrome, offset, ...props }, ref) => {
    const { isRTL } = useDirection();
    return (React.createElement(SideNavigationContainer, { ...props, ref: ref, offset: offset },
        React.createElement(SideNavigationList, null, items.map(({ label, href, active, onClick, onLinkClick, ...itemProps }, index) => {
            if (!href.startsWith('#')) {
                warnOnce('SideNavigationAnchor', `SideNavigationAnchor: Items should only be anchors: ${label} doesn't start with "#".`);
            }
            const handleClick = (e) => {
                onClick === null || onClick === void 0 ? void 0 : onClick(e);
                onLinkClick === null || onLinkClick === void 0 ? void 0 : onLinkClick(index);
            };
            return (React.createElement(LinkWrapper, { key: `side-navigation-item-${index}`, isRTL: isRTL },
                React.createElement(StyledLink, { key: `side-navigation-anchor-${index}`, href: href, onClick: handleClick, selected: active, ...itemProps }, label),
                active && (React.createElement(ActiveBorder, { layout: true, layoutId: "active", monochrome: monochrome, transition: createTransition({ duration: MotionDurationsSeconds.normal }), isRTL: isRTL }))));
        }))));
});
SideNavigationAnchor.displayName = 'SideNavigationAnchor';
export default SideNavigationAnchor;
const LinkWrapper = styled.li(({ theme, isRTL }) => css(css `
      position: relative;
      height: auto;
      border-left: ${createCustomSpacing(2 / 16)} solid ${theme.sideNavigation.border.color};

      ${responsiveStyles({
    padding: {
        mobile: `${createCustomSpacing(11 / 16)} 0 ${createCustomSpacing(11 / 16)} ${Spacings.medium}`,
        desktop: `${Spacings.xSmall} 0 ${Spacings.xSmall} ${Spacings.medium}`,
    },
})}
    `, isRTL &&
    css `
        border-left: none;
        border-right: ${createCustomSpacing(2 / 16)} solid ${theme.sideNavigation.border.color};

        ${responsiveStyles({
        padding: {
            mobile: `${createCustomSpacing(11 / 16)} ${Spacings.medium} ${createCustomSpacing(11 / 16)} 0`,
            desktop: `${Spacings.xSmall} ${Spacings.medium} ${Spacings.xSmall} 0`,
        },
    })}
      `));
const StyledLink = styled(Link)(({ theme, selected }) => css(css `
      text-decoration: none;

      &:hover {
        text-decoration: none;
      }

      &:active {
        color: ${theme.sideNavigation.link.selectedColor};
      }
    `, selected &&
    css `
        color: ${theme.sideNavigation.link.selectedColor};
      `));
const ActiveBorder = styled(motion.div)(({ theme, monochrome, isRTL }) => css(css `
        position: absolute;
        top: 0;
        left: -${createCustomSpacing(2 / 16)};
        height: 100%;
        width: ${createCustomSpacing(2 / 16)};
        z-index: 1;
        background: ${monochrome
    ? theme.sideNavigation.border.monochromeSelectedColor
    : theme.sideNavigation.border.selectedColor};
      `, isRTL &&
    css `
          left: auto;
          right: -${createCustomSpacing(2 / 16)};
        `));
